import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { useLang } from "../i18n";
import { SOLUTIONS } from "../content/solutions";
import { SECTORS } from "../content/sectors";
import { Logo } from "./Logo";

export const Header = () => {
  const { lang, t, p, switchTo } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: p(""), label: t.nav.home, testid: "nav-link-home" },
    { to: p("a-propos"), label: t.nav.about, testid: "nav-link-about" },
    { to: p("solutions"), label: t.nav.solutions, testid: "nav-link-solutions", dropdown: SOLUTIONS.map((s) => ({ to: p(`solutions/${s.slug}`), label: s[lang].title })) },
    { to: p("secteurs"), label: t.nav.sectors, testid: "nav-link-sectors", dropdown: SECTORS.map((s) => ({ to: p(`secteurs/${s.slug}`), label: s[lang].title })) },
    { to: p("infrastructure"), label: t.nav.infrastructure, testid: "nav-link-infrastructure" },
  ];

  const activeCls = ({ isActive }) =>
    `text-sm font-bold tracking-wide whitespace-nowrap transition-colors hover:text-[#FF9900] ${isActive ? "text-[#FF6600]" : "text-white/85"}`;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
          scrolled || open ? "bg-[#08090A]/90 backdrop-blur-xl border-b border-white/10 shadow-2xl" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between gap-4">
          <Link to={p("")} data-testid="nav-brand-logo" aria-label="Bula Telecoms — accueil"><Logo compact /></Link>

          <nav className="hidden lg:flex items-center gap-6" aria-label="Navigation principale">
            {links.map((l) =>
              l.dropdown ? (
                <div key={l.testid} className="relative group">
                  <NavLink to={l.to} data-testid={l.testid} className={activeCls}>
                    <span className="inline-flex items-center gap-1">{l.label}<ChevronDown size={14} className="opacity-70" /></span>
                  </NavLink>
                  <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 absolute left-0 top-full pt-3 w-80">
                    <div className="bg-[#101214] border border-white/10 rounded-xl shadow-2xl p-2">
                      {l.dropdown.map((d) => (
                        <Link key={d.to} to={d.to} className="block px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-[#FF6600]/15 transition-colors">{d.label}</Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink key={l.testid} to={l.to} data-testid={l.testid} className={activeCls} end={l.to === p("")}>{l.label}</NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <div className="flex items-center rounded-full border border-white/15 overflow-hidden" role="group" aria-label="Langue / Language">
              <button data-testid="lang-switcher-fr" onClick={() => switchTo("fr")}
                className={`px-2.5 py-1 text-xs font-bold transition-colors ${lang === "fr" ? "bg-[#FF6600] text-[#08090A]" : "text-white/70 hover:text-white"}`}>FR</button>
              <button data-testid="lang-switcher-en" onClick={() => switchTo("en")}
                className={`px-2.5 py-1 text-xs font-bold transition-colors ${lang === "en" ? "bg-[#FF6600] text-[#08090A]" : "text-white/70 hover:text-white"}`}>EN</button>
            </div>
            <Link to={p("verifier-la-couverture")} data-testid="nav-cta-coverage-check"
              className="text-xs font-bold text-white/85 border border-white/20 hover:border-[#FF6600] hover:text-[#FF9900] rounded-full px-4 py-2 transition-colors">
              {t.nav.coverage}
            </Link>
            <Link to={p("demander-un-devis")} data-testid="nav-cta-request-quote"
              className="text-xs font-black bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] rounded-full px-4 py-2 transition-colors shadow-[0_0_18px_rgba(255,102,0,0.35)]">
              {t.nav.quote}
            </Link>
          </div>

          <button className="lg:hidden text-white p-2" data-testid="mobile-menu-button" aria-label={t.nav.menu} onClick={() => setOpen(true)}>
            <Menu size={26} />
          </button>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[60] bg-[#08090A] flex flex-col" data-testid="mobile-menu" role="dialog" aria-modal="true">
          <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
            <Logo compact />
            <button onClick={() => setOpen(false)} aria-label={t.nav.close} data-testid="mobile-menu-close" className="text-white p-2"><X size={26} /></button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-1" aria-label="Menu mobile">
            {links.map((l) => (
              <div key={l.testid}>
                <NavLink to={l.to} end={l.to === p("")} className={({ isActive }) =>
                  `block py-2.5 text-2xl font-black tracking-tight ${isActive ? "text-[#FF6600]" : "text-white"}`}>
                  {l.label}
                </NavLink>
                {l.dropdown && (
                  <div className="pl-4 pb-2 flex flex-col">
                    {l.dropdown.map((d) => (
                      <Link key={d.to} to={d.to} className="py-1.5 text-sm text-white/60 hover:text-[#FF9900]">{d.label}</Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
          <div className="px-6 pb-8 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-white/70 text-sm"><Globe size={16} className="text-[#FF6600]" />
              <button data-testid="lang-switcher-fr-mobile" onClick={() => switchTo("fr")} className={`font-bold ${lang === "fr" ? "text-[#FF6600]" : ""}`}>Français</button>
              <span>/</span>
              <button data-testid="lang-switcher-en-mobile" onClick={() => switchTo("en")} className={`font-bold ${lang === "en" ? "text-[#FF6600]" : ""}`}>English</button>
            </div>
            <Link to={p("verifier-la-couverture")} data-testid="mobile-cta-coverage" className="text-center font-bold text-white border border-white/25 rounded-full px-5 py-3">{t.nav.coverage}</Link>
            <Link to={p("demander-un-devis")} data-testid="mobile-cta-quote" className="text-center font-black bg-[#FF6600] text-[#08090A] rounded-full px-5 py-3">{t.nav.quote}</Link>
          </div>
        </div>
      )}
    </>
  );
};
